import React, { useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { PillBadge } from '@xo-union/tk-component-badges';
import BlankButton from '@xo-union/component-blank-button';

import styles from './styles.scss';
import { PillProps, DatePreference, DateFormInitialVal } from './types';

const SEASON = DatePreference.Season;
const EXACT = DatePreference.Exact;
const RANGE = DatePreference.Range;
const TBA = DatePreference.Tba;
const NONE = DatePreference.None;

const DEFAULT_PILLS = [SEASON, EXACT, RANGE, TBA];

const valueToText = {
  [SEASON]: 'Season & Year',
  [EXACT]: 'Exact Date',
  [RANGE]: 'Multi Date',
  [TBA]: 'Not Sure',
  [NONE]: '',
};

export const DatePrefPillGroup: React.FC<PillProps> = ({ currentView, onClick, config }) => {
  const { setValues, values } = useFormikContext();
  const formValues = useMemo(() => values as DateFormInitialVal, [values]);
  const onPillClick = useCallback(
    (value: DatePreference): void => {
      if (currentView !== value) {
        let formatValue = formValues?.format;
        if (value === EXACT) formatValue = 'natural';
        if (value === RANGE) formatValue = 'range';
        // set hideDate for select/deselect Not Sure Yet
        setValues({
          ...formValues,
          format: formatValue,
          hideDate: value === TBA,
          preference: value,
        });
        onClick(value);
      }
    },
    [currentView, formValues, onClick, setValues]
  );

  const pillClassName = React.useCallback(
    (value: DatePreference): string => {
      let className = `${styles.pills}`;
      if (value === currentView) className += ` ${styles.activePill}`;
      return className;
    },
    [currentView]
  );

  const pills = React.useMemo(() => {
    let p = DEFAULT_PILLS;
    if (!config.showTBA) {
      p = p.filter(pill => pill !== TBA);
    }
    return p;
  }, [config]);

  return (
    <div className={styles.pillGroup}>
      {pills.map(p => (
        <BlankButton
          key={p}
          data-testid={`pill-${valueToText[p].toLowerCase().replace(/\s/g, '-')}`}
          onClick={(e: any): void => {
            e.preventDefault();
            onPillClick(p);
          }}
        >
          <PillBadge className={pillClassName(p)}>{valueToText[p]}</PillBadge>
        </BlankButton>
      ))}
    </div>
  );
};
